import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/** @extends {Component<{dialog: HTMLDialogElement, items: HTMLElement, itemTemplate: HTMLTemplateElement, count: HTMLElement, total: HTMLElement}>} */
class BPCartConfirmation extends Component {
  requiredRefs = ['dialog', 'items', 'itemTemplate', 'count', 'total'];
  #controller;
  #request = 0;
  #presented = 0;
  #trigger;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.refs.dialog.addEventListener('close', () => {
      if (this.#trigger?.isConnected) this.#trigger.focus({ preventScroll: true });
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.refs.dialog.open) this.refs.dialog.close();
    super.disconnectedCallback();
  }

  #onCartUpdate = async (event) => {
    const controller = this.#controller;
    const request = ++this.#request;
    const trigger = document.activeElement;
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    try {
      const result = await event.promise;
      if (controller.signal.aborted || !this.isConnected || !result?.cart) return;
      for (const badge of this.closest('.shopify-section')?.querySelectorAll('.bp-bag-count') || []) {
        badge.textContent = String(result.cart.totalQuantity);
      }
      const detail = result.detail;
      if (event.action !== 'add' || detail?.didError !== false || detail.source !== 'product-form-component' || !Array.isArray(detail.addedItems) || request < this.#presented) return;
      const added = detail.addedItems;
      if (!added.length) return;
      const items = Array.isArray(detail.items) ? detail.items : [];
      const currency = result.cart.cost.totalAmount.currencyCode;
      const fragment = document.createDocumentFragment();
      for (const original of added) {
        const item = items.find((line) => line.key === original.key) || original;
        const id = String(item.variant_id || item.id);
        const quantity = event.lines.filter((line) => String(line.merchandiseId).split('/').pop() === id).reduce((sum, line) => sum + Number(line.quantity), 0);
        if (quantity < 1) continue;
        const row = this.refs.itemTemplate.content.cloneNode(true);
        const title = row.querySelector('[data-item-title]');
        title.textContent = item.product_title || item.title;
        const url = new URL(item.url || Theme.routes.cart_url, location.origin);
        title.href = url.origin === location.origin ? url.href : Theme.routes.cart_url;
        const image = row.querySelector('[data-item-image]');
        const imageURL = item.featured_image?.url || item.image;
        if (imageURL) image.src = imageURL;
        else image.hidden = true;
        const options = Array.isArray(item.options_with_values) ? item.options_with_values : [];
        row.querySelector('[data-item-options]').textContent = item.product_has_only_default_variant ? '' : options.length ? options.map((option) => `${option.name}: ${option.value}`).join(' · ') : item.variant_title || '';
        row.querySelector('[data-item-quantity]').textContent = `${this.dataset.quantityLabel}: ${quantity}`;
        row.querySelector('[data-item-price]').textContent = this.#money(item.final_price ?? item.price, currency);
        fragment.append(row);
      }
      if (!fragment.childElementCount) return;
      if (sourceModal?.open) {
        await new Promise((resolve) => sourceModal.addEventListener('close', resolve, { once: true, signal: controller.signal }));
      }
      if (controller.signal.aborted || !this.isConnected || request < this.#presented) return;
      this.#presented = request;
      this.refs.items.replaceChildren(fragment);
      this.refs.count.textContent = `(${result.cart.totalQuantity})`;
      this.refs.total.textContent = this.#money(detail.cartTotal, currency);
      if (!this.refs.dialog.open) {
        this.#trigger = trigger instanceof HTMLElement ? trigger : null;
        this.refs.dialog.showModal();
      }
    } catch {
      return;
    }
  };

  #money(value, currency) {
    const formatted = formatMoney(Number(value), this.dataset.moneyFormat, currency);
    return new DOMParser().parseFromString(formatted, 'text/html').body.textContent;
  }

  close() {
    this.refs.dialog.close();
  }

  /** @param {MouseEvent} event */
  closeOnBackdrop(event) {
    const dialog = this.refs.dialog;
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  }
}

if (!customElements.get('bp-cart-confirmation')) customElements.define('bp-cart-confirmation', BPCartConfirmation);
